import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { getGame, updateGame, getTeam } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { User, Printer, Play, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, X } from 'lucide-react';
import { cn } from '../lib/utils';

// Field positions configuration
const FIELD_POSITIONS = {
  P: { x: 50, y: 58, label: 'P', name: 'Pitcher' },
  C: { x: 50, y: 82, label: 'C', name: 'Catcher' },
  '1B': { x: 72, y: 52, label: '1B', name: '1st Base' },
  '2B': { x: 58, y: 38, label: '2B', name: '2nd Base' },
  '3B': { x: 28, y: 52, label: '3B', name: '3rd Base' },
  SS: { x: 42, y: 38, label: 'SS', name: 'Shortstop' },
  LF: { x: 22, y: 22, label: 'LF', name: 'Left Field' },
  CF: { x: 50, y: 12, label: 'CF', name: 'Center Field' },
  RF: { x: 78, y: 22, label: 'RF', name: 'Right Field' }
};

// Click-to-reorder player row
const BattingOrderRow = ({ player, index, total, onMoveUp, onMoveDown, onRemove }) => (
  <div className={cn(
    "flex items-center gap-2 bg-white rounded-lg p-2 shadow border-l-4",
    index < 9 ? "border-green-500" : "border-amber-500"
  )}>
    <span className="w-6 h-6 bg-green-900 text-white rounded text-xs flex items-center justify-center font-bold">
      {index + 1}
    </span>
    <span className="text-sm font-medium flex-1 truncate">{player.name}</span>
    <span className="text-xs text-stone-500">#{player.jersey_number}</span>
    <div className="flex flex-col gap-0.5 print:hidden">
      <button
        onClick={() => onMoveUp(index)}
        disabled={index === 0}
        className="h-5 w-5 flex items-center justify-center text-stone-400 hover:text-green-700 disabled:opacity-30"
      >
        <ArrowUp size={12} />
      </button>
      <button
        onClick={() => onMoveDown(index)}
        disabled={index === total - 1}
        className="h-5 w-5 flex items-center justify-center text-stone-400 hover:text-green-700 disabled:opacity-30"
      >
        <ArrowDown size={12} />
      </button>
    </div>
    <button
      onClick={() => onRemove(player.id)}
      className="w-5 h-5 text-red-500 hover:bg-red-50 rounded flex items-center justify-center print:hidden"
    >
      <X size={12} />
    </button>
  </div>
);

// Field position marker
const FieldPosition = ({ position, player, onRemove }) => {
  const pos = FIELD_POSITIONS[position];
  return (
    <div
      className={cn(
        "absolute w-12 h-12 rounded-full flex flex-col items-center justify-center text-xs font-bold transition-all",
        player ? "bg-green-600 text-white" : "bg-stone-300 text-stone-600 border-2 border-dashed border-stone-400"
      )}
      style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: 'translate(-50%, -50%)' }}
      onClick={() => player && onRemove(position)}
    >
      <span>{pos.label}</span>
      {player && <span className="text-[10px] truncate max-w-full px-1">{player.jersey_number}</span>}
    </div>
  );
};

export default function PreGamePage() {
  const navigate = useNavigate();
  const printRef = useRef();
  const { currentGameId, currentTeamId } = useGameStore();
  const [game, setGame] = useState(null);
  const [team, setTeam] = useState(null);
  const [battingOrder, setBattingOrder] = useState([]);
  const [fieldPositions, setFieldPositions] = useState({});
  const [currentInning, setCurrentInning] = useState(1);
  const [inningPositions, setInningPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentGameId || !currentTeamId) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [currentGameId, currentTeamId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [gameData, teamData] = await Promise.all([
        getGame(currentGameId),
        getTeam(currentTeamId)
      ]);
      setGame(gameData);
      setTeam(teamData);
      
      if (gameData.batting_order?.length > 0) {
        const orderedPlayers = gameData.batting_order
          .map(id => teamData.players?.find(p => p.id === id))
          .filter(Boolean);
        setBattingOrder(orderedPlayers);
      }
      
      if (gameData.inning_positions) {
        setInningPositions(gameData.inning_positions);
        setFieldPositions(gameData.inning_positions[1] || {});
      } else if (gameData.field_positions) {
        setFieldPositions(gameData.field_positions);
        setInningPositions({ 1: gameData.field_positions });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load game data');
    } finally {
      setLoading(false);
    }
  };

  // Batting order management
  const handleMoveUp = (index) => {
    if (index <= 0) return;
    const newOrder = [...battingOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setBattingOrder(newOrder);
  };

  const handleMoveDown = (index) => {
    if (index >= battingOrder.length - 1) return;
    const newOrder = [...battingOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setBattingOrder(newOrder);
  };

  const handleAddToBattingOrder = (player) => {
    if (battingOrder.find(p => p.id === player.id)) {
      toast.error('Player already in lineup');
      return;
    }
    setBattingOrder([...battingOrder, player]);
  };

  const handleRemoveFromBattingOrder = (playerId) => {
    setBattingOrder(battingOrder.filter(p => p.id !== playerId));
  };

  // Field position management
  const handleAssignPosition = (playerId, position) => {
    const newPositions = { ...fieldPositions };
    Object.keys(newPositions).forEach(pos => {
      if (newPositions[pos] === playerId) delete newPositions[pos];
    });
    newPositions[position] = playerId;
    setFieldPositions(newPositions);
    setInningPositions(prev => ({ ...prev, [currentInning]: newPositions }));
  };

  const handleRemoveFromPosition = (position) => {
    const newPositions = { ...fieldPositions };
    delete newPositions[position];
    setFieldPositions(newPositions);
    setInningPositions(prev => ({ ...prev, [currentInning]: newPositions }));
  };

  const handleInningChange = (inning) => {
    setInningPositions(prev => ({ ...prev, [currentInning]: fieldPositions }));
    setCurrentInning(inning);
    const newPositions = inningPositions[inning] || inningPositions[currentInning] || {};
    setFieldPositions(newPositions);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const allInningPositions = { ...inningPositions, [currentInning]: fieldPositions };
      await updateGame(currentGameId, {
        batting_order: battingOrder.map(p => p.id),
        field_positions: fieldPositions,
        inning_positions: allInningPositions
      });
      toast.success('Pre-game setup saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => { window.print(); };
  const getPlayerById = (id) => team?.players?.find(p => p.id === id);
  const benchPlayers = team?.players?.filter(p => !battingOrder.find(bp => bp.id === p.id)) || [];
  const availableForField = team?.players || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="w-12 h-12 border-4 border-green-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 p-4" data-testid="pregame-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 print:hidden">
          <div>
            <h1 className="text-2xl font-extrabold text-green-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              PRE-GAME SETUP
            </h1>
            <p className="text-stone-600 text-sm">
              {game?.is_home ? 'vs' : '@'} {game?.opponent_name} • {game?.date}
            </p>
            <p className="text-xs text-stone-400 mt-1">Tap players to add • Use arrows to reorder • Tap position to assign</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handlePrint} variant="outline" data-testid="print-btn">
              <Printer size={16} className="mr-1" /> Print
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-500" data-testid="save-pregame-btn">
              {saving ? 'Saving...' : 'Save Setup'}
            </Button>
            <Button onClick={() => navigate('/game-mode')} className="bg-amber-500 hover:bg-amber-400" data-testid="start-game-btn">
              <Play size={16} className="mr-1" /> Start Game
            </Button>
          </div>
        </div>

        {/* Print Header */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold text-center">{team?.name} - Game Lineup</h1>
          <p className="text-center">{game?.is_home ? 'vs' : '@'} {game?.opponent_name} • {game?.date}</p>
        </div>

        {/* Main Content - 3 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" ref={printRef}>
          
          {/* Left Column - Batting Order */}
          <Card className="border-0 shadow-md print:shadow-none print:border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                BATTING ORDER ({battingOrder.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto print:max-h-none print:overflow-visible">
              <div className="space-y-1">
                {battingOrder.map((player, index) => (
                  <BattingOrderRow
                    key={player.id}
                    player={player}
                    index={index}
                    total={battingOrder.length}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    onRemove={handleRemoveFromBattingOrder}
                  />
                ))}
              </div>
              
              {/* Bench */}
              {benchPlayers.length > 0 && (
                <div className="mt-4 pt-4 border-t border-stone-200">
                  <p className="text-xs text-stone-500 uppercase mb-2">Bench - Tap to Add</p>
                  <div className="space-y-1">
                    {benchPlayers.map(player => (
                      <button
                        key={player.id}
                        onClick={() => handleAddToBattingOrder(player)}
                        className="w-full flex items-center gap-2 p-2 bg-stone-100 hover:bg-green-50 hover:border-green-400 rounded border border-stone-200 text-left text-sm print:hidden transition-all"
                      >
                        <User size={14} className="text-stone-400" />
                        <span>{player.name}</span>
                        <span className="text-xs text-stone-400">#{player.jersey_number}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Center Column - Field Diagram */}
          <Card className="border-0 shadow-md print:shadow-none print:border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  FIELD POSITIONS
                </CardTitle>
                <div className="flex items-center gap-2 print:hidden">
                  <Button variant="ghost" size="sm" onClick={() => handleInningChange(Math.max(1, currentInning - 1))} disabled={currentInning <= 1}>
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="text-sm font-medium">Inning {currentInning}</span>
                  <Button variant="ghost" size="sm" onClick={() => handleInningChange(currentInning + 1)} disabled={currentInning >= 9}>
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="relative bg-green-700 rounded-lg overflow-hidden" style={{ paddingBottom: '100%' }}>
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
                  <path d="M 5 95 Q 50 -10 95 95 Z" fill="#2d5a27" />
                  <path d="M 30 70 L 50 45 L 70 70 L 50 85 Z" fill="#c9a66b" />
                  <line x1="50" y1="85" x2="70" y2="65" stroke="white" strokeWidth="0.5" />
                  <line x1="70" y1="65" x2="50" y2="45" stroke="white" strokeWidth="0.5" />
                  <line x1="50" y1="45" x2="30" y2="65" stroke="white" strokeWidth="0.5" />
                  <line x1="30" y1="65" x2="50" y2="85" stroke="white" strokeWidth="0.5" />
                  <rect x="48" y="83" width="4" height="4" fill="white" transform="rotate(45 50 85)" />
                  <rect x="68" y="63" width="3" height="3" fill="white" transform="rotate(45 70 65)" />
                  <rect x="48" y="43" width="3" height="3" fill="white" transform="rotate(45 50 45)" />
                  <rect x="28" y="63" width="3" height="3" fill="white" transform="rotate(45 30 65)" />
                  <circle cx="50" cy="60" r="3" fill="#c9a66b" />
                </svg>
                
                {Object.keys(FIELD_POSITIONS).map(pos => (
                  <FieldPosition
                    key={pos}
                    position={pos}
                    player={getPlayerById(fieldPositions[pos])}
                    onRemove={handleRemoveFromPosition}
                  />
                ))}
              </div>
              
              <div className="hidden print:block mt-4">
                <p className="font-bold mb-2">Inning {currentInning} Positions:</p>
                <div className="grid grid-cols-3 gap-1 text-sm">
                  {Object.entries(FIELD_POSITIONS).map(([pos, config]) => {
                    const player = getPlayerById(fieldPositions[pos]);
                    return (
                      <div key={pos}>
                        <span className="font-medium">{config.name}:</span> {player?.name || '-'}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Player Assignment */}
          <Card className="border-0 shadow-md print:shadow-none print:border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                ASSIGN POSITIONS
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto print:hidden">
              <p className="text-xs text-stone-500 mb-3">Select a position for each player using the dropdown</p>
              
              <div className="space-y-1">
                {availableForField.map(player => {
                  const assignedPos = Object.entries(fieldPositions).find(([pos, pid]) => pid === player.id)?.[0];
                  
                  return (
                    <div
                      key={player.id}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded border transition-all",
                        assignedPos 
                          ? "bg-green-100 border-green-300" 
                          : "bg-white border-stone-200"
                      )}
                    >
                      <span className="w-8 h-8 bg-green-900 text-white rounded flex items-center justify-center text-sm font-bold">
                        {player.jersey_number}
                      </span>
                      <span className="flex-1 text-sm font-medium">{player.name}</span>
                      {assignedPos && (
                        <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">{assignedPos}</span>
                      )}
                      <Select
                        value={assignedPos || ''}
                        onValueChange={(pos) => handleAssignPosition(player.id, pos)}
                      >
                        <SelectTrigger className="w-24 h-8 text-xs">
                          <SelectValue placeholder="Pos" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(FIELD_POSITIONS).map(([pos, config]) => (
                            <SelectItem key={pos} value={pos}>{pos} - {config.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </CardContent>
            
            <CardContent className="hidden print:block">
              <p className="font-bold mb-2">Team Roster:</p>
              <div className="text-sm">
                {team?.players?.map(p => (
                  <div key={p.id}>#{p.jersey_number} {p.name}</div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          /* PreGame specific: make field diagram print-friendly */
          .bg-green-700 { background: #2d5a27 !important; }
          /* Hide arrows and remove buttons in print */
          button[class*="ArrowUp"], button[class*="ArrowDown"],
          button[class*="X"] { display: none !important; }
          /* Show all items without scroll */
          .max-h-\\[500px\\] { max-height: none !important; overflow: visible !important; }
        }
      `}</style>
    </div>
  );
}
